import { Badge, Tooltip } from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useUpgradeContext } from 'src/hooks/useUpgradeContext';

interface SeatsLeftBadgeProps {
  accountId?: string;
  maxSeats?: number | null;
  currentSeats?: number;
}

/**
 * Helper function to calculate seats left.
 *
 * Returns null when:
 * - No seat limit exists (maxSeats is null/undefined)
 * - Current seat data is unavailable (currentSeats undefined)
 * - Invalid or negative seat count (currentSeats < 0)
 *
 * @returns The number of seats left, or null if inputs are invalid.
 */
export const getSeatsLeft = (
  maxSeats?: number | null,
  currentSeats?: number,
): number | null => {
  if (
    maxSeats === null ||
    maxSeats === undefined ||
    currentSeats === undefined ||
    currentSeats < 0
  ) {
    return null;
  }
  return maxSeats - currentSeats;
};

/**
 * Renders a badge displaying the number of seats left.
 *
 * Conditions:
 * - Displays only if at least one seat is taken and seat limit exists.
 */
export const SeatsLeftBadge = ({
  accountId,
  maxSeats,
  currentSeats,
}: SeatsLeftBadgeProps) => {
  const { upgradeRoute } = useUpgradeContext();

  const seatsLeft = getSeatsLeft(maxSeats, currentSeats);

  const shouldRender =
    seatsLeft !== null &&
    seatsLeft >= 0 &&
    currentSeats !== undefined &&
    currentSeats >= 1;

  if (!shouldRender) {
    return null;
  }

  const variant = seatsLeft === 0 ? 'beta' : 'neutralOutlined';

  const seatText = seatsLeft === 1 ? 'SEAT REMAINING' : 'SEATS REMAINING';

  const badgeElement = (
    <Badge variant={variant}>
      {seatsLeft} {seatText}
    </Badge>
  );

  return (
    <PermissionGuard
      accountId={accountId}
      accountPermissions="canSetConstrainedPlan"
      fallback={badgeElement}
    >
      <Tooltip label="Unlock more features">
        <Link to={upgradeRoute}>{badgeElement}</Link>
      </Tooltip>
    </PermissionGuard>
  );
};
